import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-xl text-sm font-semibold ring-offset-background transition-all duration-300 ease-premium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-soft hover:shadow-elevated hover:-translate-y-0.5",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-soft",
        outline: "border-2 border-border bg-background/50 backdrop-blur-sm hover:bg-secondary hover:border-primary/30 hover:-translate-y-0.5 transition-all",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-subtle",
        ghost: "hover:bg-secondary/80 hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline font-medium",
        christmas: "btn-christmas text-primary-foreground font-semibold tracking-wide uppercase text-xs",
        premium: "btn-premium text-primary-foreground font-semibold tracking-wide",
        gold: "bg-gradient-gold text-gold-foreground hover:opacity-90 font-semibold shadow-gold hover:-translate-y-0.5",
        "christmas-outline": "border-2 border-primary/30 bg-primary/5 text-primary hover:bg-primary hover:text-primary-foreground font-semibold backdrop-blur-sm",
        "glass": "bg-card/70 backdrop-blur-xl border border-border/40 text-foreground hover:bg-card/90 hover:border-border/60 shadow-soft hover:-translate-y-0.5 hover:shadow-elevated",
      },
      size: {
        default: "h-12 px-6 py-3",
        sm: "h-10 rounded-lg px-4 text-xs",
        lg: "h-14 rounded-xl px-8 text-base",
        xl: "h-16 rounded-2xl px-10 text-lg tracking-wide [&_svg]:size-5",
        icon: "h-12 w-12 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
